import { Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { HourlyPayLabel } from "@src/appV2/redesign/Pay/HourlyPayLabel";
import { TotalPayLabel } from "@src/appV2/redesign/Pay/TotalPayLabel";
import { getFormattedShiftPayBreakdown } from "@src/appV2/redesign/Shift/getFormattedShiftPayBreakdown";
import type { WorkerShift } from "@src/appV2/Shifts/Shift/types";

import { Button } from "../../../components/Button";
import { StickyFooter } from "../../../components/StickyFooter";
import { SHIFT_DETAILS_FOOTER_LOG_EVENT_SOURCE_UI } from "../constants";
import { useFooterPrimaryAction } from "../hooks/useShiftActionVisibility";

export interface NonInstantPayFooterProps {
  shift: WorkerShift;
  currentDate: Date;
  onUpdateETA: () => void;
  onCancelShift: () => void;
  onSentHome: () => void;
  onUploadTimesheet: () => void;
  isUploadingTimesheet: boolean;
  isMissedPunchRequestEnabled: boolean;
}

function NonInstantPayFooter(props: NonInstantPayFooterProps) {
  const {
    shift,
    currentDate,
    onUpdateETA,
    onCancelShift,
    onSentHome,
    onUploadTimesheet,
    isUploadingTimesheet,
    isMissedPunchRequestEnabled,
  } = props;

  const primaryAction = useFooterPrimaryAction({ shift, currentDate });

  const payBreakdown = getFormattedShiftPayBreakdown(
    shift.offer?.pay.value,
    shift.finalPay,
    shift.originalAmount
  );

  const renderPrimaryButton = () => {
    if (!primaryAction) {
      return null;
    }

    const { action } = primaryAction;

    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (action) {
      case "cancelShift": {
        return (
          <Button
            fullWidth
            variant="outlined"
            size="large"
            sx={{
              color: (theme) => theme.palette.error.light,
              borderColor: (theme) => theme.palette.border?.primary,
            }}
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_CANCEL_SHIFT_TAPPED, {
                shiftId: shift._id,
                sourceUI: SHIFT_DETAILS_FOOTER_LOG_EVENT_SOURCE_UI,
              });
              onCancelShift();
            }}
          >
            Cancel shift
          </Button>
        );
      }

      case "uploadTimesheet": {
        return (
          <LoadingButton
            fullWidth
            isLoading={isUploadingTimesheet}
            size="large"
            variant="contained"
            onClick={() => {
              onUploadTimesheet();
            }}
          >
            {isMissedPunchRequestEnabled ? "Request shift payment" : "Upload Timesheet"}
          </LoadingButton>
        );
      }

      case "updateEta": {
        return (
          <Button
            fullWidth
            size="large"
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_LATE_FOR_SHIFT_TAPPED, {
                shiftId: shift._id,
                sourceUI: SHIFT_DETAILS_FOOTER_LOG_EVENT_SOURCE_UI,
              });
              onUpdateETA();
            }}
          >
            Update ETA
          </Button>
        );
      }

      case "sentHome": {
        return (
          <Button
            fullWidth
            size="large"
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_SENT_HOME_TAPPED, {
                shiftId: shift._id,
                sourceUI: SHIFT_DETAILS_FOOTER_LOG_EVENT_SOURCE_UI,
              });
              onSentHome();
            }}
          >
            I was sent home
          </Button>
        );
      }

      default: {
        return null;
      }
    }
  };

  return (
    <StickyFooter>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ paddingX: 2, marginBottom: 5 }}
      >
        <HourlyPayLabel size="large" hourlyPay={payBreakdown?.hourlyPay} />
        <Stack direction="row" spacing={2} alignItems="center">
          <TotalPayLabel totalPay={payBreakdown?.totalPay} />
          {/* Non-instant pay shifts don't show instant pay indicator */}
        </Stack>
      </Stack>

      {renderPrimaryButton()}
    </StickyFooter>
  );
}

export { NonInstantPayFooter };
